import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Users,
  Settings,
  FileText,
  Bell,
  BarChart3,
  HelpCircle,
  Palette,
  Home,
  UserCheck,
  MessageSquare,
  FolderOpen,
  TrendingUp,
  Building2,
  LifeBuoy,
  CreditCard,
  Activity,
  DollarSign,
} from "lucide-react";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const navigationItems = [
  {
    title: "Dashboard",
    href: "/",
    icon: Home,
  },
  {
    title: "Users",
    href: "/users",
    icon: Users,
  },
  {
    title: "Admins",
    href: "/admins",
    icon: UserCheck,
  },
  {
    title: "Establishments & Staff",
    href: "/establishments",
    icon: Building2,
  },
  {
    title: "Notifications",
    href: "/notifications",
    icon: Bell,
  },
  {
    title: "Templates",
    href: "/templates",
    icon: FileText,
  },
  {
    title: "Groups",
    href: "/groups",
    icon: MessageSquare,
  },
  {
    title: "Content",
    href: "/content",
    icon: FolderOpen,
  },
  {
    title: "FAQs",
    href: "/faqs",
    icon: HelpCircle,
  },
  {
    title: "Help & Support",
    href: "/help-support",
    icon: LifeBuoy,
  },
  {
    title: "Subscription Plans",
    href: "/subscription-plans",
    icon: CreditCard,
  },
  {
    title: "Real-Time Dashboard",
    href: "/real-time-dashboard",
    icon: Activity,
  },
  {
    title: "Payment Processing",
    href: "/payment-processing",
    icon: DollarSign,
  },
  {
    title: "User Analytics",
    href: "/user-analytics",
    icon: TrendingUp,
  },
  {
    title: "Statistics",
    href: "/statistics",
    icon: BarChart3,
  },
  {
    title: "Appearance",
    href: "/appearance",
    icon: Palette,
  },
  {
    title: "Settings",
    href: "/settings",
    icon: Settings,
  },
];

export function Sidebar({ isOpen, onToggle, isCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { currentTheme } = useTheme();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed left-0 top-0 z-50 h-full transform bg-background border-r transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ",
          isOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "w-16" : "w-60"
        )}
        style={
          {
            "--primary": currentTheme.colors.primary,
            "--secondary": currentTheme.colors.secondary,
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between  border-b">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className="hidden lg:flex"
            title={isCollapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
          >
            {isCollapsed ? <Logo size={42} showText={false} /> : <Logo size={42} showText={true} />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-4">
          <nav className="space-y-2 flex flex-col ml-1.5">
            {navigationItems.map(item => {
              const isActive = location.pathname === item.href;
              return (
                <Link key={item.href} to={item.href}>
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    className={cn(
                      "w-full h-6",
                      isCollapsed
                        ? "justify-start items-center px-2  rounded-lg w-4 h-6"
                        : "justify-start space-x-3 px-3",
                      isActive && "bg-primary text-primary-foreground"
                    )}
                    style={
                      isActive
                        ? {
                            backgroundColor: currentTheme.colors.primary,
                            color: "white",
                          }
                        : {}
                    }
                    title={isCollapsed ? item.title : undefined}
                  >
                    <item.icon className="w-4 h-4" />
                    {!isCollapsed && <span>{item.title}</span>}
                  </Button>
                </Link>
              );
            })}
          </nav>

          <Separator className="my-4" />

          {/* Theme Info */}
          {!isCollapsed && (
            <div className="px-3 py-2">
              <div className="text-xs text-muted-foreground mb-2">Current Theme</div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currentTheme.colors.primary }}
                />
                <span className="text-sm font-medium capitalize">
                  {currentTheme.colors.primary === "#ff6b35"
                    ? "Orange"
                    : currentTheme.colors.primary === "#dc2626"
                    ? "Red"
                    : currentTheme.colors.primary === "#059669"
                    ? "Green"
                    : currentTheme.colors.primary === "#2563eb"
                    ? "Blue"
                    : currentTheme.colors.primary === "#7c3aed"
                    ? "Purple"
                    : currentTheme.colors.primary === "#d97706"
                    ? "Cream"
                    : "Custom"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {currentTheme.isDarkMode ? "Dark Mode" : "Light Mode"}
              </div>
            </div>
          )}

          {/* Collapsed theme indicator */}
          {isCollapsed && (
            <div className="flex justify-center py-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: currentTheme.colors.primary }}
                title={`${currentTheme.isDarkMode ? "Dark" : "Light"} Mode`}
              />
            </div>
          )}
        </ScrollArea>
      </div>
    </>
  );
}
